import { useMemo } from 'react';
import { Combobox } from '@/shared/components/ui/combobox';
import { currencyFromSp, formatMoney } from '@/shared/lib/money';
import type { CompraListItem } from '../api';
import { saldoRestante } from '../lib/saldo';

/**
 * Picker for the parent compra when registering a Nota de Crédito.
 *
 * Filters the cached list of compras to rows that admins can legally
 * offset with a new NC: `Status_CO` in Pendiente / Pago Parcial, not
 * already linked to another NC, and with positive remaining balance.
 * Mirrors PowerApps Screen_Compras.pa.yaml line 732.
 *
 * The selected parent's `moneda` and `restante` are useful to the
 * caller (for live-validating the NC magnitude and locking the NC's
 * Moneda_CO), so the `onChange` handler receives the full
 * `CompraListItem` — the server uses its `id` as `NotaCreditoID_CO`.
 */
export function ParentCompraPicker({
  compras,
  value,
  onChange,
  loading,
  disabled,
}: {
  compras: CompraListItem[];
  value: CompraListItem | null;
  onChange: (next: CompraListItem | null) => void;
  loading: boolean;
  disabled?: boolean;
}) {
  const eligible = useMemo(() => {
    return compras.filter((c) => {
      const status = c.fields.Status_CO ?? '';
      if (status !== 'Pendiente' && status !== 'Pago Parcial') return false;
      // Exclude NCs (compras that are themselves linked to a parent).
      if ((c.fields.NotaCreditoID_CO ?? '').trim()) return false;
      return saldoRestante(c) > 0.01;
    });
  }, [compras]);

  return (
    <Combobox<CompraListItem>
      value={value}
      onChange={onChange}
      items={eligible}
      getKey={(c) => c.id}
      getLabel={(c) => {
        const comp = `${c.fields.Comprobante_CO ?? ''} ${c.fields.NumeroComprobante_CO ?? ''}`.trim();
        return `${c.fields.Proveedor_CO ?? '—'} — ${comp || 'sin nº'}`;
      }}
      getSublabel={(c) => {
        const restante = saldoRestante(c);
        // Use the shared SP-currency parser so MXN parent compras don't
        // get mis-labeled as ARS in the picker's "Saldo" sublabel.
        const cur = currencyFromSp(c.fields.Moneda_CO);
        return `Saldo: ${formatMoney(restante, cur)}`;
      }}
      matcher={(c, q) =>
        (c.fields.Proveedor_CO ?? '').toLowerCase().includes(q) ||
        (c.fields.NumeroComprobante_CO ?? '').toLowerCase().includes(q)
      }
      placeholder={
        loading
          ? 'Cargando…'
          : eligible.length === 0
            ? 'Sin facturas elegibles'
            : 'Seleccionar factura…'
      }
      disabled={disabled || loading || eligible.length === 0}
    />
  );
}

/**
 * Compute the saldo available on a parent compra. Exposed for callers
 * that need to validate NC magnitude against a specific row.
 */
export function restanteOf(compra: CompraListItem): number {
  return saldoRestante(compra);
}
